import { useQuery } from '@tanstack/react-query';
import { Card, Skeleton, EmptyState } from '../../../ui';
import { leadsService } from '../../../services/leads.service';
import { useProduct } from '../../../contexts/ProductContext';

export function EquipeTab() {
  const { selectedProductId } = useProduct();

  const { data: leadsResp, isLoading } = useQuery({
    queryKey: ['insights-equipe', selectedProductId],
    queryFn: () => leadsService.list({ productId: selectedProductId, limit: 1000 }),
    enabled: !!selectedProductId,
  });

  if (!selectedProductId) {
    return <EmptyState title="Selecione um produto" body="Métricas da equipe são por produto." />;
  }
  if (isLoading) {
    return <Skeleton height={300} />;
  }

  const leads: any[] =
    (leadsResp as any)?.leads ??
    (leadsResp as any)?.items ??
    (Array.isArray(leadsResp) ? leadsResp : []);

  // Aggregate per user
  const map = new Map<
    number,
    { id: number; name: string; total: number; won: number; lost: number; revenue: number }
  >();
  for (const l of leads) {
    const userId = l.assignedUserId;
    if (userId === null || userId === undefined) {
      continue;
    }
    const userName = l.assignedUser?.name ?? `User #${userId}`;
    if (!map.has(userId)) {
      map.set(userId, { id: userId, name: userName, total: 0, won: 0, lost: 0, revenue: 0 });
    }
    const row = map.get(userId)!;
    row.total++;
    const stageType = l.stage?.type ?? l.stageType;
    if (stageType === 'WON') {
      row.won++;
      row.revenue += Number(l.dealValue ?? 0);
    } else if (stageType === 'LOST') {
      row.lost++;
    }
  }

  const rows = Array.from(map.values()).sort((a, b) => b.total - a.total);

  if (rows.length === 0) {
    return (
      <EmptyState
        title="Sem dados de equipe"
        body="Quando vendedores forem atribuídos a leads, métricas aparecem aqui."
      />
    );
  }

  return (
    <Card padding="none">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-subtle">
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 font-medium text-ink-secondary">Vendedor</th>
              <th className="text-right px-4 py-3 font-medium text-ink-secondary">Leads</th>
              <th className="text-right px-4 py-3 font-medium text-ink-secondary">Ganhos</th>
              <th className="text-right px-4 py-3 font-medium text-ink-secondary">Perdas</th>
              <th className="text-right px-4 py-3 font-medium text-ink-secondary">Win rate</th>
              <th className="text-right px-4 py-3 font-medium text-ink-secondary">Receita</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const closed = r.won + r.lost;
              const wr = closed > 0 ? Math.round((r.won / closed) * 100) : 0;
              return (
                <tr key={r.id}>
                  <td className="px-4 py-3 text-ink-primary font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-ink-secondary text-right font-mono">{r.total}</td>
                  <td className="px-4 py-3 text-accent text-right font-mono">{r.won}</td>
                  <td className="px-4 py-3 text-danger text-right font-mono">{r.lost}</td>
                  <td className="px-4 py-3 text-ink-secondary text-right font-mono">{wr}%</td>
                  <td className="px-4 py-3 text-ink-primary text-right font-mono">
                    R$ {r.revenue.toLocaleString('pt-BR')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
